import React from "react";

export default function EmojiTutorial({ game }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white p-4 pb-12">
      
      {/* ── HEADER ── */}
      <div className="text-center mb-8 pt-6">
        <div className="text-5xl mb-3">🎯</div>
        <h1 className="text-3xl sm:text-4xl font-black text-cyan-300 tracking-tight">
          How to Play
        </h1>
        <p className="text-cyan-400/70 text-sm mt-1">Emoji Grid</p>
      </div>

      <div className="max-w-lg mx-auto space-y-5">

        {/* ── INTRODUCTION ── */}
        <section className="bg-slate-900/70 border border-slate-700 rounded-3xl p-5">
          <h2 className="text-cyan-300 font-bold text-lg mb-2 flex items-center gap-2">
            <span>📖</span> Introduction
          </h2>
          <p className="text-slate-200 text-sm leading-relaxed">
            Welcome to <span className="text-cyan-300 font-semibold">Emoji Grid</span>!  
            Emojis keep scrolling across the screen. Your job is to quickly tap the ones that match your chosen category and fill the grid before time runs out.
          </p>
        </section>

        {/* ── OBJECTIVE ── */}
        <section className="bg-slate-900/70 border border-slate-700 rounded-3xl p-5">
          <h2 className="text-cyan-300 font-bold text-lg mb-2 flex items-center gap-2">
            <span>🎯</span> Objective
          </h2>
          <p className="text-slate-200 text-sm leading-relaxed">
            Fill the entire grid (3×3, 4×4 or 5×5) with correct emojis.  
            The faster and more accurately you do it, the higher your score.
          </p>
        </section>

        {/* ── HOW TO PLAY ── */}
        <section className="bg-slate-900/70 border border-slate-700 rounded-3xl p-5">
          <h2 className="text-cyan-300 font-bold text-lg mb-3 flex items-center gap-2">
            <span>🕹️</span> How to Play
          </h2>

          <div className="space-y-3">
            {[
              {
                icon: "1️⃣",
                title: "Choose your settings",
                desc: "Pick category (Animals, Food, etc.), grid size and speed.",
              },
              {
                icon: "2️⃣",
                title: "Watch the conveyor",
                desc: "Emojis continuously scroll from right to left.",
              },
              {
                icon: "3️⃣",
                title: "Tap only matching emojis",
                desc: "Only tap emojis that belong to your chosen category.",
              },
              {
                icon: "4️⃣",
                title: "Fill the grid",
                desc: "Each correct tap adds the emoji to your grid. Fill it completely to finish.",
              },
              {
                icon: "5️⃣",
                title: "Avoid mistakes",
                desc: "Wrong taps, double taps, or letting a correct emoji scroll off cost points.",
              },
            ].map((step) => (
              <div key={step.icon} className="flex gap-3">
                <div className="text-xl flex-shrink-0">{step.icon}</div>
                <div>
                  <div className="font-semibold text-white text-sm">{step.title}</div>
                  <div className="text-slate-400 text-xs mt-0.5 leading-snug">
                    {step.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── POINT RULES ── */}
        <section className="bg-slate-900/70 border border-slate-700 rounded-3xl p-5">
          <h2 className="text-cyan-300 font-bold text-lg mb-3 flex items-center gap-2">
            <span>💰</span> Point Rules
          </h2>

          <div className="space-y-2 text-sm">
            {/* Positive */}
            <div className="flex justify-between items-center bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2">
              <span className="text-emerald-300">Correct tap</span>
              <span className="font-bold text-emerald-400">+100</span>
            </div>
            <div className="flex justify-between items-center bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2">
              <span className="text-emerald-300">Combo bonus (each extra)</span>
              <span className="font-bold text-emerald-400">+20</span>
            </div>
            <div className="flex justify-between items-center bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-3 py-2">
              <span className="text-yellow-300">Golden Emoji</span>
              <span className="font-bold text-yellow-400">+500</span>
            </div>
            <div className="flex justify-between items-center bg-purple-500/10 border border-purple-500/20 rounded-xl px-3 py-2">
              <span className="text-purple-300">Treasure Emoji</span>
              <span className="font-bold text-purple-400">+1000</span>
            </div>

            {/* Negative */}
            <div className="flex justify-between items-center bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 mt-3">
              <span className="text-red-300">Wrong emoji</span>
              <span className="font-bold text-red-400">-10</span>
            </div>
            <div className="flex justify-between items-center bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
              <span className="text-red-300">Duplicate tap</span>
              <span className="font-bold text-red-400">-10</span>
            </div>
            <div className="flex justify-between items-center bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
              <span className="text-red-300">Missed (let it scroll away)</span>
              <span className="font-bold text-red-400">-10</span>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-3 text-center">
            Already collected emojis that scroll past → No penalty (smart play!)
          </p>
        </section>

        {/* ── EXAMPLE ── */}
        <section className="bg-slate-900/70 border border-slate-700 rounded-3xl p-5">
          <h2 className="text-cyan-300 font-bold text-lg mb-3 flex items-center gap-2">
            <span>📌</span> Example
          </h2>

          <div className="bg-slate-950/60 rounded-2xl p-4 text-sm space-y-2">
            <p className="text-slate-300">
              You chose <span className="text-cyan-300 font-medium">Animals</span> category.
            </p>
            <p className="text-slate-300">
              🐶 appears → <span className="text-emerald-400">Tap it</span> → <span className="text-emerald-400">+100</span>
            </p>
            <p className="text-slate-300">
              🍕 appears → <span className="text-red-400">Don’t tap</span> (wrong category)
            </p>
            <p className="text-slate-300">
              🐶 appears again → <span className="text-red-400">Don’t tap</span> (already collected)
            </p>
            <p className="text-slate-300">
              🌟 Golden 🦁 appears → <span className="text-yellow-400">Tap fast!</span> → <span className="text-yellow-400">+600</span>
            </p>
          </div>
        </section>

        {/* ── START BUTTON ── */}
        <div className="pt-4 flex justify-center">
          <button
            onClick={() => game.setScreen("home")}
            className="w-full max-w-xs py-4 rounded-2xl 
                       bg-gradient-to-r from-cyan-400 to-violet-500 
                       text-black font-black text-lg
                       shadow-lg shadow-cyan-500/20
                       active:scale-95 transition-all
                       hover:brightness-110"
          >
            Got it! Let’s Play 🚀
          </button>
        </div>

        <p className="text-center text-xs text-slate-500 pt-2">
          Made for kids • Fun for everyone
        </p>
      </div>
    </div>
  );
}